// ────────────────────────────────────────────────────────────────────
// What a spreadsheet IS, decided from its own column headers.
//
// The import console used to put three buttons under every uploaded file and
// ask "นี่คือ?". The system already knew: it parses the file to list its
// columns, and the moment you answered "ค่าอ่านความหนา" the next screen proved
// it — ImportModal pre-selects cml_no / thickness / measured_at from those same
// headers. The knowledge existed one screen too late. RegisterImportModal did
// not have it at all; its mapping started blank.
//
// So the keyword sets live here, once. The console uses them to DECIDE, both
// mapping modals use them to PRE-FILL. Two copies of "what a thickness column
// looks like" would drift, and drift between the two halves of one flow is what
// most of this codebase's recent bugs turned out to be.
//
// That last sentence was aspirational when it was written: ImportModal kept its
// own guessCol and its own inline keyword literals, and RegisterImportModal
// still opened blank. Both now seed from seedThicknessMapping /
// seedRegisterMapping below, so the claim is enforced by there being nowhere
// else for the keywords to live.
//
// Deliberately conservative: a signature needs BOTH halves, a sheet matching
// both signatures is "unknown", and an unrecognisable sheet is "unknown". The
// point is to stop asking questions with a knowable answer — not to guess
// confidently and be wrong, which is worse than asking.
// ────────────────────────────────────────────────────────────────────

import type { IntakeImportMapping, RegisterMapping } from "@/lib/types";

/**
 * The column `kws` names, or "".
 *
 * Keyword order beats column order: the lists below are written specific-first,
 * and a loose keyword must not win just because it happens to appear in an
 * earlier column. "mm" is a substring of "Comment", so scanning columns first
 * mapped thickness to a remarks column whenever remarks came first.
 *
 * `taken` holds columns already claimed by another field. One column cannot be
 * two fields, and without that rule "Nominal" contains "min", so t_min stole
 * the nominal-thickness column — a wrong t_min is not a cosmetic mis-map, it
 * changes every remaining-life number computed from the import.
 */
export function guessCol(cols: string[], kws: string[], taken: ReadonlySet<string> = new Set()): string {
  for (const k of kws) {
    const hit = cols.find((c) => !taken.has(c) && c.toLowerCase().includes(k));
    if (hit) return hit;
  }
  return "";
}

/** Thickness survey — one row per CML reading. */
export const THICKNESS_KW = {
  cml_no: ["cml", "point", "tml", "loc"],
  thickness: ["thk", "thick", "reading", "wall", "mm"],
  measured_at: ["date", "when", "inspect"],
  t_nominal: ["nom", "original"],
  t_min: ["min", "retire"],
} as const;

/** Asset register — one row per asset, carrying where it lives. */
export const REGISTER_KW = {
  plant: ["plant", "site", "facility", "โรงงาน"],
  area: ["area", "unit", "zone", "พื้นที่"],
  asset_tag: ["tag", "asset", "equip", "อุปกรณ์"],
  name: ["name", "descr", "ชื่อ"],
  asset_class: ["class", "type", "ชนิด"],
  design_code: ["code", "design", "standard"],
  service: ["service", "fluid", "medium"],
  material: ["material", "matl", "spec"],
  first_service_date: ["commission", "install", "service date", "in-service"],
  drawing_no: ["drawing", "dwg", "p&id", "pid"],
  corrosion_loop: ["loop", "circuit"],
  damage_mechanism: ["damage", "mechanism", "dm"],
} as const;

export type ImportKind = "thickness" | "register" | "unknown";

export interface Classification {
  kind: ImportKind;
  /** The headers the decision rested on — shown to the reader so the guess can
   *  be checked without opening the file. Empty when kind is "unknown". */
  matched: string[];
}

export function classifyColumns(cols: string[]): Classification {
  const cml = guessCol(cols, [...THICKNESS_KW.cml_no]);
  const thk = guessCol(cols, [...THICKNESS_KW.thickness]);
  const tag = guessCol(cols, [...REGISTER_KW.asset_tag]);
  const plant = guessCol(cols, [...REGISTER_KW.plant]);
  const area = guessCol(cols, [...REGISTER_KW.area]);

  const looksThickness = !!cml && !!thk;
  const looksRegister = !!tag && (!!plant || !!area);

  // Both signatures present: genuinely ambiguous. Asking is the right move
  // here — this is the case the three buttons were always for.
  if (looksThickness && looksRegister) return { kind: "unknown", matched: [] };

  if (looksThickness) {
    const date = guessCol(cols, [...THICKNESS_KW.measured_at]);
    return { kind: "thickness", matched: [cml, thk, date].filter(Boolean) };
  }
  if (looksRegister) {
    return { kind: "register", matched: [tag, plant, area].filter(Boolean) };
  }
  return { kind: "unknown", matched: [] };
}

// ── Pre-fills ───────────────────────────────────────────────────────
// Both mapping modals seed from here rather than from their own copies of the
// keyword lists. The classifier and the pre-fill are then, provably, the same
// knowledge: a sheet cannot be called a thickness survey on one screen and open
// with a blank cml_no on the next.
//
// Required fields seed to "" and optional ones to undefined, deliberately —
// a blank optional would be posted as a column name of "".

/** Fields already claimed, so no column is offered twice. */
function claim(taken: Set<string>, col: string): string {
  if (col) taken.add(col);
  return col;
}

export function seedThicknessMapping(cols: string[]): Partial<IntakeImportMapping> {
  const taken = new Set<string>();
  return {
    cml_no: claim(taken, guessCol(cols, [...THICKNESS_KW.cml_no], taken)),
    thickness: claim(taken, guessCol(cols, [...THICKNESS_KW.thickness], taken)),
    measured_at: claim(taken, guessCol(cols, [...THICKNESS_KW.measured_at], taken)),
    t_nominal: claim(taken, guessCol(cols, [...THICKNESS_KW.t_nominal], taken)) || undefined,
    t_min: claim(taken, guessCol(cols, [...THICKNESS_KW.t_min], taken)) || undefined,
  };
}

/** Optional register fields, in the order the modal lists them. */
const REGISTER_OPTIONAL: readonly (keyof typeof REGISTER_KW)[] = [
  "name", "asset_class", "design_code", "service", "material",
  "corrosion_loop", "damage_mechanism", "first_service_date", "drawing_no",
];

export function seedRegisterMapping(cols: string[]): Partial<RegisterMapping> {
  const taken = new Set<string>();
  // asset_tag first: it is the identity column, and its keywords are the most
  // specific of the three. "Unit Tag" must become the tag, not the area.
  const asset_tag = claim(taken, guessCol(cols, [...REGISTER_KW.asset_tag], taken));
  const out: Partial<RegisterMapping> = {
    plant: claim(taken, guessCol(cols, [...REGISTER_KW.plant], taken)),
    area: claim(taken, guessCol(cols, [...REGISTER_KW.area], taken)),
    asset_tag,
  };
  for (const k of REGISTER_OPTIONAL) {
    const hit = claim(taken, guessCol(cols, [...REGISTER_KW[k]], taken));
    if (hit) out[k] = hit;
  }
  return out;
}
